import React, { useState, useMemo, useRef, useEffect, useCallback, useImperativeHandle } from 'react'
import { useUncontrolled } from 'uncontrollable'
import useGetData from 'hooks/useGetData'
import useDidUpdate from 'hooks/useDidUpdate'
import { useIntl } from 'react-intl'
import camelCaseKeys from 'utils/camelCaseKeys'
import { getIsMail } from 'utils/documents'
import documentActionTypes from 'const/documentActionTypes'
import KanBoIcon from 'components/KanBoIcon'
import MailPreview from 'components/MailPreview'
import DocumentReferences from 'components/DocumentReferences'
import InfiniteScroll, { DefaultLoaderComponent } from 'components/InfiniteScroll'
import Alert from 'components/Alert'
import { ViewBody } from 'components/Sidebar'
import sweetalert from 'sweetalert2'
import useChannel from 'hooks/useChannel'
import withLeftSidebar, { LeftSidebarProps } from 'components/Sidebar/withLeftSidebar'
import DocumentRename from 'components/DocumentRename'
import documentListChannelData from './documentListChannelData'
import useSaga from 'hooks/useSaga'
import styled from 'styled-components'
import Button from 'components/Button'
import ModalDocumentList from './ModalDocumentList'
import { useDispatch } from 'react-redux'
import requestRemoveFolder from '../sagas/requestRemoveFolder'
import requestRemoveDocument from '../sagas/requestRemoveDocument'
import { FormattedMessage } from 'components/Intl'
import { ModalMenu } from 'components/Modal'
import {
  MenuItem
} from 'components/Menu'
import PathBreadcrumb from 'components/PathBreadcrumb'
import DocumentItem from './DocumentItem'

const Header = styled(ViewBody)`
  margin-top: 5px;
  margin-bottom: 10px;
`

const SortBy = styled.span`
  float: right;
  margin-left: 10px;
`

const LoaderComponent = styled(DefaultLoaderComponent)`
  margin-top: 10px;
  margin-bottom: 10px;
`

const NothingToShow = styled(ViewBody)`
  margin-top: 10px;
  margin-bottom: 10px;
`

const LIMIT = 20

const onChannelJoinError = () => {}

type Ref = {
  refresh: () => void;
}

type ChildrenData = {
  render: () => React.ReactElement;
  data?: any
}

type Props = LeftSidebarProps & {
  documentComponent?: React.ComponentType<React.ComponentProps<typeof DocumentItem>>;
  showMax?: number;
  title: React.ReactNode;

  documentSourceId: number;
  url?: string;
  query?: string;
  onQueryChange?: (query: string) => void;
  path?: any[];
  defaultPath?: any[];
  onPathChange?: (path: any[]) => void;
  sort?: string;
  defaultSort?: string;
  onSortChange?: (sort: string) => void;

  children?: (data: ChildrenData) => React.ReactElement;
}

export const DocumentList = React.forwardRef<Ref, Props>((props, ref) => {
  const {
    leftSidebar,

    documentComponent: DocumentComponent = DocumentItem,
    showMax,
    title,

    documentSourceId,
    url,
    query,
    onQueryChange,
    path = [],
    onPathChange,
    sort = props.defaultSort || 'default',
    onSortChange,

    children,
  } = useUncontrolled(props, {
    query: 'onQueryChange',
    path: 'onPathChange',
    sort: 'onSortChange',
  })

  const intl = useIntl()

  const dispatch = useDispatch()

  const [mailPreview, setMailPreview] = useState<any>()
  const [showDocumentReferences, setShowDocumentReferences] = useState<any>()

  const [_fetch, setFetch] = useState(true)
  const [cursor, setCursor] = useState(undefined)
  const [documents, setDocuments] = useState<any[]>([])

  const [sortOptions, setSortOptions] = useState<any[]>()

  const [hiddenDocuments, setHiddenDocuments] = useState<string[]>([])
  const hiddenDocumentsRef = useRef<string[]>([])

  const currentPath = useMemo(() => {
    if (path.length > 0) {
      return path[path.length - 1]
    } else if (documentSourceId) {
      return {
        documentSourceId,
        url,
      }
    }
  }, [path, documentSourceId, url])

  useChannel(currentPath ? `documents-source-${currentPath.documentSourceId}` : undefined, onChannelJoinError)

  const handleChannelDataRef = useRef<any>()

  useEffect(() => {
    handleChannelDataRef.current = ({ actionName, elementName, data }) => {
      if (actionName + elementName === 'RenameFile/Data/RenameFile') {
        if (data.DocumentSourceId === currentPath?.documentSourceId) {
          setDocuments(documents => documents.map(document => {
            if (document.url === data.OldUrl) {
              return {
                ...document,
                url: data.NewUrl,
                name: data.NewName,
              }
            }

            return document
          }))
        }
      }
    }
  }, [currentPath?.documentSourceId])

  const handleChannelData = useCallback(
    (...args) => {
      handleChannelDataRef.current && handleChannelDataRef.current(...args)
    },
    []
  )

  useSaga(documentListChannelData, handleChannelData)

  useEffect(() => {
    hiddenDocumentsRef.current = hiddenDocuments
  }, [hiddenDocuments])

  const refresh = useCallback(() => {
    setDocuments([])
    setHiddenDocuments([])
    setCursor(undefined)
    setFetch(value => !value)
  }, [])

  useImperativeHandle(ref, () => ({
    refresh
  }))

  useDidUpdate(() => {
    refresh()
  }, [query, currentPath?.documentSourceId, currentPath?.url, showMax, sort])

  const prevDocumentsDataRef = useRef(null)

  const args = useMemo(() => ({
    documentsSourceId: currentPath?.documentSourceId,
    query,
    url: currentPath?.url,
    cursor,
    sort: (sort !== 'default' && !query) ? sort : undefined,
    limit: showMax ? showMax + 1 : LIMIT
  }), [_fetch]) // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data: DocumentsData,
    error,
    isLoading,
  }: {
    data: any;
    error: any;
    isLoading: boolean;
  } = useGetData(currentPath ? (query ? 'SearchDocumentsSourceFiles' : 'FilesList') : undefined, args)

  useEffect(() => {
    if (
      DocumentsData &&
      DocumentsData !== prevDocumentsDataRef.current
    ) {
      setDocuments([
        ...documents,
        ...DocumentsData.Items.map(Document => ({
          url: Document.Url,
          lastModificationTimestamp: Document.LastModificationTimestamp,
          name: Document.Name,
          documentCount: Document.Count,
          objectType: Document.ObjectType,
          size: Document.Size,
          canRead: Document.CanRead,
          exists: Document.Exists,
          thumbnailUrl: Document.ThumbnailUrl,
          actions: Document.Actions ? Document.Actions.map(camelCaseKeys) : []
        }))
      ])
      setCursor(DocumentsData.Cursor)
      setSortOptions(DocumentsData.SortOptions)
    }

    prevDocumentsDataRef.current = DocumentsData
  }, [currentPath?.documentSourceId, documents, DocumentsData])

  const handleLoadMore = useCallback(
    () => {
      setFetch(value => !value)
    },
    []
  )

  const handleIndexClick = useCallback(index => {
    onPathChange?.(path.slice(0, index + 1))
  }, [path, onPathChange])

  const handleCloseMailPreview = useCallback(() => {
    setMailPreview(null)
  }, [])

  const handleDocumentReferencesClose = useCallback(() => {
    setShowDocumentReferences(null)
  }, [])

  const handleDocumentRename = useCallback(({ promise, documentSourceId: _documentSourceId, url, name, newName }) => {
    if (currentPath?.documentSourceId === _documentSourceId) {
      setDocuments(documents => documents.map(document => {
        if (document.url === url) {
          return {
            ...document,
            name: newName,
          }
        }

        return document
      }))

      promise.then(newName => {
        setDocuments(documents => documents.map(document => {
          if (document.url === url) {
            return {
              ...document,
              name: newName || name,
            }
          }

          return document
        }))
      })
    }
  }, [currentPath?.documentSourceId])

  const allSortOptions = useMemo(() => [
    ...(props.defaultSort ? [] : [
      {
        Value: 'default',
        DisplayName: {
          MessageId: 'DOCUMENTS_SOURCE_SORT_DEFAULT',
        }
      }
    ]),
    ...(sortOptions || []),
  ], [props.defaultSort, sortOptions])

  const selectedSortOption = useMemo(() => {
    return allSortOptions.find(SortOption => SortOption.Value === sort)
  }, [allSortOptions, sort])

  const filteredDocuments = useMemo(() => {
    return documents.filter(document => !hiddenDocuments.includes(document.url))
  }, [documents, hiddenDocuments])

  const slicedDocuments = useMemo(() => {
    if (!showMax) return filteredDocuments

    return filteredDocuments.slice(0, showMax)
  }, [filteredDocuments, showMax])

  const render = () => {
    return (
      <div>
        <Header>
          <PathBreadcrumb
            path={path}
            onIndexClick={handleIndexClick}
          />
          {(!error && sortOptions && sortOptions.length > 0) &&      
            <SortBy>
              <FormattedMessage id='DOCUMENTS_SOURCE_SORT' />:
              {' '}
              <ModalMenu
                attachment='top center'
                targetAttachment='bottom center'
              >
                {selectedSortOption &&
                  <a>
                    <FormattedMessage
                      id={selectedSortOption.DisplayName.MessageId}
                      values={selectedSortOption.DisplayName.Values}
                    />
                  </a>
                }
                {allSortOptions.map(SortOption =>
                  <MenuItem
                    key={SortOption.Value}
                    active={SortOption.Value === sort}
                    onClick={() => {
                      onSortChange?.(SortOption.Value)
                    }}
                  >
                    <FormattedMessage
                      id={SortOption.DisplayName.MessageId}
                      values={SortOption.DisplayName.Values}
                    />
                  </MenuItem>
                )}
              </ModalMenu>
            </SortBy>
          }
        </Header>
        <InfiniteScroll
          isLoading={isLoading}
          isLoaded={!cursor || (showMax && filteredDocuments.length >= showMax)}
          onLoadMore={handleLoadMore}
          loaderComponent={LoaderComponent}
        >
          {slicedDocuments.map(document => {
            const isFolder = document.objectType === 1

            if (isFolder) {
              const onFolderClick = () => {
                onPathChange?.([
                  ...(path || []),
                  {
                    documentSourceId: currentPath?.documentSourceId,
                    name: document.name,
                    url: document.url,
                  }
                ])
                onQueryChange?.('')
              }

              const onRemoveClick = () => {
                sweetalert({
                  text: intl.formatMessage({
                    id: 'DOCUMENT_REMOVE_FOLDER_QUESTION'
                  }, {
                    name: document.name
                  }),
                  confirmButtonText: intl.formatMessage({ id: 'DELETE' }),
                  cancelButtonText: intl.formatMessage({ id: 'CANCEL' }),
                  showCancelButton: true,
                  type: 'question'
                }).then(() => {
                  setHiddenDocuments([
                    ...hiddenDocumentsRef.current,
                    document.url
                  ])

                  const promise: any = dispatch(requestRemoveFolder({
                    documentSourceId: currentPath?.documentSourceId,
                    url: document.url,
                  }))

                  promise.then(() => {
                    refresh()
                  }, () => {
                    setHiddenDocuments(
                      hiddenDocumentsRef.current.filter(hiddenDocumentUrl => hiddenDocumentUrl !== document.url)
                    )
                  })
                }, () => {})
              }

              let menu = [{
                icon: <KanBoIcon fixedWidth name='kanbo-16-delete' />,
                title: <FormattedMessage id='DELETE' />,
                onClick: onRemoveClick
              }]

              return (
                <DocumentComponent
                  {...document}
                  key={document.url}
                  type='folder'
                  documentSourceId={currentPath?.documentSourceId}
                  menu={menu}
                  onClick={onFolderClick}
                />
              )
            } else {
              const onOpenClick =
                getIsMail(document.name)
                  ? () => {
                    setMailPreview({
                      name: document.name,
                      url: document.url,
                      sourceId: currentPath?.documentSourceId
                    })
                  }
                  : () => {
                    const action = document.actions.find(action => action.default)

                    if (!action) return

                    const actionType = documentActionTypes[action.type]

                    if (!actionType) return

                    actionType.onClick(action)
                  }

              const onReferencesClick = () => {
                setShowDocumentReferences({
                  documentSourceId: currentPath?.documentSourceId,
                  url: document.url,
                  name: document.name
                })
              }

              const onRemoveClick = () => {
                sweetalert({
                  text: intl.formatMessage({
                    id: 'DOCUMENT_REMOVE_FROM_SOURCE_QUESTION'
                  }, {
                    name: document.name
                  }),
                  confirmButtonText: intl.formatMessage({ id: 'DELETE' }),
                  cancelButtonText: intl.formatMessage({ id: 'CANCEL' }),
                  showCancelButton: true,
                  type: 'question'
                }).then(() => {
                  setHiddenDocuments([
                    ...hiddenDocumentsRef.current,
                    document.url
                  ])

                  const promise: any = dispatch(requestRemoveDocument({
                    documentSourceId: currentPath?.documentSourceId,
                    url: document.url,
                  }))

                  promise.then(() => {
                    refresh()
                  }, () => {
                    setHiddenDocuments(
                      hiddenDocumentsRef.current.filter(hiddenDocumentUrl => hiddenDocumentUrl !== document.url)
                    )
                  })
                }, () => {})
              }

              let menu = document.actions.reduce((acc, action) => {
                const actionType = documentActionTypes[action.type]

                if (!actionType) return acc

                acc.push({
                  icon: <KanBoIcon fixedWidth name={actionType.iconName} />,
                  title: <FormattedMessage id={actionType.formattedMessageId} />,
                  onClick: () => actionType.onClick(action, document)
                })

                return acc
              }, [])

              menu.push({
                icon: <KanBoIcon fixedWidth name='kanbo-16-letter-count' />,
                title: <FormattedMessage id='DOCUMENT_RENAME' />,
                onClick: () => {
                  leftSidebar.push(() =>
                    <DocumentRename
                      documentSourceId={currentPath?.documentSourceId}
                      url={document.url}
                      name={document.name}
                      onRename={handleDocumentRename}
                    />)
                }
              })
              menu.push({
                icon: <KanBoIcon fixedWidth name='kanbo-16-document-references' />,
                title: <FormattedMessage id='DOCUMENT_REFERENCES' />,
                onClick: onReferencesClick
              })
              menu.push({
                icon: <KanBoIcon fixedWidth name='kanbo-16-delete' />,
                title: <FormattedMessage id='DELETE' />,
                onClick: onRemoveClick
              })

              return (
                <DocumentComponent
                  {...document}
                  key={document.url}
                  type='document'
                  documentSourceId={currentPath?.documentSourceId}
                  menu={menu}
                  onClick={onOpenClick}
                />
              )
            }
          })}
          {(cursor === null && filteredDocuments.length === 0) &&
            <NothingToShow>
              <FormattedMessage id='NOTHING_TO_SHOW' />
            </NothingToShow>
          }
          {(showMax && filteredDocuments.length > showMax) &&
            <Button
              block
              bsStyle='link'
              onClick={() => {
                leftSidebar.push(() =>
                  <ModalDocumentList
                    documentSourceId={documentSourceId}
                    url={url}
                    title={title}
                    path={path}
                    sort={sort}
                  />
                )
              }}
            >
              <FormattedMessage id='DOCUMENTS_SOURCE_SHOW_MORE' />
            </Button>
          }
          {error &&
            <ViewBody>
              <Alert bsStyle='danger'>
                <FormattedMessage id='DOCUMENTS_SOURCE_ACCESS_DENIED' />
              </Alert>
            </ViewBody>
          }
          {mailPreview &&
            <MailPreview
              name={mailPreview.name}
              url={mailPreview.url}
              sourceId={mailPreview.sourceId}
              onClose={handleCloseMailPreview}
            />
          }
          {showDocumentReferences &&
            <DocumentReferences
              {...showDocumentReferences}
              onClose={handleDocumentReferencesClose}
            />
          }
        </InfiniteScroll>
      </div>
    )
  }

  if (children) {
    return children({ render, data: DocumentsData })
  } else {
    return render()
  }
})

export default withLeftSidebar(DocumentList)
